/**
 * Add filter / Edit filter: the dialog that makes a slicer.
 *
 * Three choices, in the order people make them: **what kind** of filter (List,
 * Dropdown, Range, Dates - picked like a plot type, from cards), **which
 * column** (Explore's own `<column-combobox>`: fuzzy search, arrow keys, type
 * badges, multi-ship coverage), and an optional **label** to show instead of the
 * column's name.
 *
 * Nothing is chosen for you. Either order works: a kind narrows the columns to
 * the ones it can use, and a column greys out the kinds it cannot be shown as,
 * each with a tooltip saying why. A list of a column with too many values is
 * refused when you press Add, with the reason, rather than guessed around.
 *
 * A List or Dropdown starts loading its values as soon as both are picked, so
 * a column with thousands of them says so - and that it will be slow - before
 * Add, while Range is still one click away. Add reuses the same cached load.
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import '../../ui/column-combobox.ts';
import type { PoolColumn } from '../ship/ship-columns.ts';
import { addSlicerTile, updateSlicerTile, type DashboardTile } from './dashboard-state.ts';
import { MAX_SLICER_VALUES, checkSlicer, familyOf, filterColumnPool, slicerOptions } from './slicer-state.ts';
import { modeFits, type SlicerFamily, type SlicerMode } from './variables.ts';

const stroke = (d: TemplateResult): TemplateResult => html`<svg
  class="plot-type-icon"
  viewBox="0 0 15 15"
  aria-hidden="true"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  ${d}
</svg>`;

interface Kind {
  mode: SlicerMode;
  label: string;
  hint: string;
  icon: TemplateResult;
}

const KINDS: Kind[] = [
  {
    mode: 'list',
    label: 'List',
    hint: 'Tick values in a list that is always open',
    icon: stroke(svg`<rect x="2" y="2.5" width="3" height="3" rx="0.6" /><path d="M7 4h6" />
      <rect x="2" y="9.5" width="3" height="3" rx="0.6" /><path d="M7 11h6" />`),
  },
  {
    mode: 'dropdown',
    label: 'Dropdown',
    hint: 'Tick values in a list that opens from one line',
    icon: stroke(svg`<rect x="1.5" y="4.5" width="12" height="6" rx="1" /><path d="M9.5 7l1.2 1.2L11.9 7" />`),
  },
  {
    mode: 'range',
    label: 'Range',
    hint: 'Drag two handles over a number, as in Explore',
    icon: stroke(svg`<path d="M1.5 7.5h12" /><rect x="3.5" y="4.5" width="2.2" height="6" rx="1" fill="currentColor" />
      <rect x="9.3" y="4.5" width="2.2" height="6" rx="1" fill="currentColor" />`),
  },
  {
    mode: 'time',
    label: 'Dates',
    hint: 'Drag two handles over a time column',
    icon: stroke(svg`<rect x="1.5" y="2.5" width="12" height="11" rx="1" /><path d="M1.5 6h12M4.5 1v3M10.5 1v3" />`),
  },
];

const FAMILY_WORD: Record<SlicerFamily, string> = {
  text: 'a text column',
  int: 'a whole-number column',
  float: 'a decimal column',
  time: 'a time column',
};

const MODE_NEEDS: Record<SlicerMode, string> = {
  list: 'any column but a time',
  dropdown: 'any column but a time',
  range: 'a number column',
  time: 'a time column',
};

@customElement('dashboard-filter-dialog')
export class DashboardFilterDialog extends LightElement {
  /** The slicer being edited, or null to add a new one. */
  @property({ attribute: false }) tile: DashboardTile | null = null;
  /** The ships on screen: the columns offered, and whose values are checked. */
  @property({ attribute: false }) ships: string[] = [];

  @state() private mode: SlicerMode | null = null;
  @state() private column = '';
  @state() private label = '';
  @state() private pool: PoolColumn[] | 'loading' | 'error' = 'loading';
  @state() private error = '';
  @state() private busy = false;
  /** How many values a List or Dropdown would load: unknown until counted. */
  @state() private values: 'idle' | 'counting' | 'few' | 'many' = 'idle';

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKeydown);
    if (this.tile?.slicer) {
      this.mode = this.tile.slicer.mode;
      this.column = this.tile.slicer.column;
      this.label = this.tile.title !== this.tile.slicer.column ? this.tile.title : '';
      this.countValues();
    }
    filterColumnPool(this.ships).then(
      (p) => (this.pool = p),
      () => (this.pool = 'error'),
    );
  }

  override disconnectedCallback(): void {
    document.removeEventListener('keydown', this.onKeydown);
    super.disconnectedCallback();
  }

  private onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.close();
  };

  private close = (): void => {
    this.dispatchEvent(new CustomEvent('filter-dialog-close', { bubbles: true }));
  };

  private columnInfo(name = this.column): PoolColumn | undefined {
    return Array.isArray(this.pool) ? this.pool.find((c) => c.name === name) : undefined;
  }

  private family(name = this.column): SlicerFamily | null {
    const c = this.columnInfo(name);
    return c ? familyOf(c.type) : null;
  }

  private pickMode(mode: SlicerMode): void {
    this.mode = mode;
    this.error = '';
    const fam = this.family();
    // A column this kind cannot use is let go rather than kept and refused later.
    if (fam && !modeFits(mode, fam)) this.column = '';
    this.countValues();
  }

  private pickColumn(name: string): void {
    this.column = name;
    this.error = '';
    this.countValues();
  }

  /** Load a List or Dropdown's values now, to warn when there are very many. */
  private countValues(): void {
    const { column, mode } = this;
    if (!column || (mode !== 'list' && mode !== 'dropdown')) {
      this.values = 'idle';
      return;
    }
    this.values = 'counting';
    slicerOptions(this.ships, column)
      .catch(() => null)
      .then((options) => {
        // A pick made while this loaded owns the note now.
        if (this.column !== column || this.mode !== mode) return;
        this.values = options?.kind === 'ready' && options.truncated ? 'many' : 'few';
      });
  }

  private renderValuesNote(): TemplateResult | typeof nothing {
    if (this.values === 'counting') {
      // The values come from the ship's whole history, so this can take a while on any column.
      return html`<p class="fdlg-note is-warning" role="status">
        <span class="spinner"></span> Reading every value of ${this.column} in the ship's history. This can take a minute.
      </p>`;
    }
    if (this.values === 'many') {
      return html`<p class="fdlg-note is-warning" role="status">
        ${this.column} has more than ${MAX_SLICER_VALUES.toLocaleString('en-US')} values, so this
        ${this.mode === 'list' ? 'list' : 'dropdown'} can be slow to open.${this.family() === 'text' ? '' : ' Range is faster.'}
      </p>`;
    }
    return nothing;
  }

  private async submit(): Promise<void> {
    const fam = this.family();
    if (!this.mode || !fam || this.busy) return;
    const def = { column: this.column, family: fam, mode: this.mode };
    this.busy = true;
    try {
      const check = await checkSlicer(this.ships, def);
      if (!check.ok) {
        this.error = check.reason;
        return;
      }
      if (this.tile) updateSlicerTile(this.tile.id, def, this.label);
      else addSlicerTile(def, this.label);
      this.close();
    } finally {
      this.busy = false;
    }
  }

  /* ── Rendering ────────────────────────────────────────── */

  private renderKinds(): TemplateResult {
    const fam = this.family();
    return html`<div class="plot-type-grid" role="radiogroup" aria-label="Kind of filter">
      ${KINDS.map((k) => {
        const unfit = !!fam && !modeFits(k.mode, fam);
        return html`<button
          class="plot-type-btn ${this.mode === k.mode ? 'active' : ''} ${unfit ? 'is-unavailable' : ''}"
          role="radio"
          aria-checked=${this.mode === k.mode ? 'true' : 'false'}
          aria-disabled=${unfit ? 'true' : 'false'}
          title=${unfit
            ? `${k.label} needs ${MODE_NEEDS[k.mode]}; ${this.column} is ${FAMILY_WORD[fam!]}`
            : k.hint}
          @click=${() => !unfit && this.pickMode(k.mode)}
        >
          ${k.icon}
          <span class="plot-type-name">${k.label}</span>
        </button>`;
      })}
    </div>`;
  }

  private renderColumn(): TemplateResult {
    if (!this.ships.length) return html`<p class="fdlg-note">Pick a ship in the toolbar first.</p>`;
    if (this.pool === 'loading') {
      return html`<p class="fdlg-note"><span class="spinner"></span> Loading columns</p>`;
    }
    if (this.pool === 'error') return html`<p class="fdlg-note is-error">Could not load the columns.</p>`;
    const mode = this.mode;
    const columns = mode ? this.pool.filter((c) => modeFits(mode, familyOf(c.type))) : this.pool;
    return html`<column-combobox
      .columns=${columns}
      .value=${this.column}
      placeholder="Search columns…"
      .select=${(name: string) => this.pickColumn(name)}
      .clear=${this.column ? () => this.pickColumn('') : null}
    ></column-combobox>`;
  }

  override render(): TemplateResult {
    const editing = !!this.tile;
    const ready = !!this.mode && !!this.column && !!this.family();
    return html`
      <div class="cbuilder-scrim" @click=${this.close}></div>
      <div class="cpick fdlg" role="dialog" aria-modal="true" aria-label=${editing ? 'Edit filter' : 'Add filter'}>
        <header class="cpick-head">
          <h2>${editing ? 'Edit filter' : 'Add filter'}</h2>
        </header>
        <div class="cpick-body fdlg-body">
          <div class="axis-block">
            <label>Kind</label>
            ${this.renderKinds()}
          </div>
          <div class="axis-block">
            <label>Column</label>
            ${this.renderColumn()}
            ${this.renderValuesNote()}
          </div>
          <div class="axis-block">
            <label for="fdlg-label">Label <span class="fdlg-optional">optional</span></label>
            <input
              id="fdlg-label"
              type="text"
              maxlength="80"
              placeholder=${this.column || 'Shown above the filter'}
              .value=${this.label}
              @input=${(e: Event) => (this.label = (e.target as HTMLInputElement).value)}
              @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && void this.submit()}
            />
          </div>
          ${this.error ? html`<p class="fdlg-note is-error" role="alert">${this.error}</p>` : nothing}
        </div>
        <footer class="cpick-foot">
          <span class="cpick-note">
            Filters narrow every chart on this tab. Each person's picks are their own.
          </span>
          <span class="cpick-foot-actions">
            <button class="dash-btn" @click=${this.close}>Cancel</button>
            <button class="dash-btn is-primary" ?disabled=${!ready || this.busy} @click=${() => void this.submit()}>
              ${this.busy ? html`<span class="spinner"></span>` : nothing}
              ${editing ? 'Save filter' : 'Add filter'}
            </button>
          </span>
        </footer>
      </div>
    `;
  }
}
